import React from "react";
import { Check, Plus, Search, Sparkle } from "lucide-react";

import { getMarketInstrumentLookup } from "../../api/client";
import { MAX_QUERY_LENGTH, TYPE_LABELS } from "./screenerModel";
import styles from "./screenerPage.module.css";

const LOOKUP_TYPES = { stock: "A股", etf: "ETF", open_fund: "场外基金", fund: "场外基金" };

// 关注按钮：已持有 / 已关注是终态，不可点；加入中禁用，防止连点发两次请求。
export function FollowButton({ code, held, watched, busy, onFollow }) {
  if (held) return <span className={`${styles.follow} ${styles.followDone}`}>已持有</span>;
  if (watched) return <span className={`${styles.follow} ${styles.followDone}`}><Check size={13} aria-hidden="true" />已关注</span>;
  return (
    <button
      type="button"
      className={styles.follow}
      disabled={busy}
      onClick={(event) => { event.stopPropagation(); onFollow(code); }}
      aria-label={`加入关注 ${code}`}
    >
      <Plus size={13} aria-hidden="true" />{busy ? "加入中" : "关注"}
    </button>
  );
}

// 输入框 + 「查找」按钮 + 输入时的名称 / 代码联想。
// Enter 与「查找」永远是同一件事：把整句话交给自然语言查找（不按内容猜意图，花不花 token 用户心里有数）；
// 想直接找标的就点联想里的那一行——它走现有的名称 / 代码搜索，免费。
export function ScreenerSearch({ variant, value, onChange, onSubmit, busy, held, watched, followBusy, onFollow, onOpenAsset }) {
  const [suggestions, setSuggestions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [looking, setLooking] = React.useState(false);
  const rootRef = React.useRef(null);
  const text = value.trim();

  React.useEffect(() => {
    if (!text || !open) {
      setSuggestions([]);
      setLooking(false);
      return undefined;
    }
    let active = true;
    setLooking(true);
    const timer = window.setTimeout(async () => {
      try {
        const payload = await getMarketInstrumentLookup({ query: text, limit: 6 });
        if (active) setSuggestions(payload?.data?.items ?? []);
      } catch {
        if (active) setSuggestions([]);  // 联想只是捷径，取不到不影响「查找」
      } finally {
        if (active) setLooking(false);
      }
    }, 200);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [text, open]);

  React.useEffect(() => {
    if (!open) return undefined;
    const close = (event) => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  function submit(event) {
    event.preventDefault();
    if (!text || busy) return;
    setOpen(false);
    onSubmit(text);
  }

  const showList = open && text && (looking || suggestions.length > 0);
  const big = variant === "home";
  return (
    <form
      ref={rootRef}
      className={`${styles.searchForm} ${big ? styles.searchBig : ""}`}
      onSubmit={submit}
      role="search"
      aria-label="查找标的"
      onKeyDown={(event) => { if (event.key === "Escape") setOpen(false); }}
    >
      <div className={styles.inputWrap}>
        <Search size={18} className={styles.searchIcon} aria-hidden="true" />
        <input
          value={value}
          maxLength={MAX_QUERY_LENGTH}
          onChange={(event) => { onChange(event.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="输入名称、代码，或用一句话描述"
          aria-label="查找标的"
          autoComplete="off"
          autoFocus={big}
        />
        {showList && (
          <div className={styles.suggest} role="listbox" aria-label="名称或代码联想">
            {suggestions.map((item) => {
              const code = item.code;
              return (
                <div
                  key={item.instrument_id ?? code}
                  className={styles.suggestRow}
                  role="option"
                  aria-selected="false"
                  tabIndex={0}
                  onClick={() => onOpenAsset(code)}
                  onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); event.stopPropagation(); onOpenAsset(code); } }}
                >
                  <span className={styles.assetLink}><span>{item.name}</span><small>{code} · {LOOKUP_TYPES[item.instrument_type] ?? TYPE_LABELS[item.instrument_type] ?? item.instrument_type}</small></span>
                  <FollowButton code={code} held={held.has(code)} watched={watched.has(code)} busy={followBusy.has(code)} onFollow={onFollow} />
                </div>
              );
            })}
            {looking && !suggestions.length && <div className={styles.suggestHint}>搜索中…</div>}
          </div>
        )}
      </div>
      <button className={styles.searchButton} type="submit" disabled={!text || busy} aria-describedby="screener-price">
        {busy ? "查找中…" : "查找"}
        <span className={styles.cost} aria-hidden="true"><Sparkle size={12} fill="currentColor" />10</span>
      </button>
      <span id="screener-price" className={styles.srOnly}>每次查找消耗 10 光点</span>
    </form>
  );
}
